import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { EmailGroup, SharedInbox } from '../components/outlook-mailbox-settings';

export interface UseChatSettingsResult {
  groups: EmailGroup[];
  selectedGroupIds: Set<string>;
  sharedInboxes: SharedInbox[];
  isLoading: boolean;
  error: string | null;
  isDirty: boolean;
  toggleGroup: (groupId: string) => void;
  selectAllGroups: () => void;
  deselectAllGroups: () => void;
  addSharedInbox: (email: string) => boolean;
  removeSharedInbox: (id: string) => void;
  toggleSharedInbox: (id: string) => void;
  save: () => void;
  resetChanges: () => void;
  refreshGroups: () => Promise<void>;
}

export interface UseChatSettingsConfig {
  graphClient: unknown;
  /** Server-persisted group IDs (from useNovaSettings via context) */
  serverSelectedGroupIds?: string[];
  /** Server-persisted shared inbox emails (from useNovaSettings via context) */
  serverSharedInboxes?: string[];
  /** Callback to persist all changes to the server in a single mutation */
  onSave?: (settings: { groupIds: string[]; sharedInboxes: string[] }) => void;
}

/**
 * Convert server-persisted email strings to SharedInbox objects for the UI.
 */
function emailsToSharedInboxes(emails: string[]): SharedInbox[] {
  return emails.map((email) => ({ id: uuidv4(), email, enabled: true }));
}

/**
 * Extract emails from SharedInbox objects for server persistence.
 */
function sharedInboxesToEmails(inboxes: SharedInbox[]): string[] {
  return inboxes.map((inbox) => inbox.email);
}

export function useChatSettings({
  graphClient,
  serverSelectedGroupIds,
  serverSharedInboxes,
  onSave: onSaveCallback,
}: UseChatSettingsConfig): UseChatSettingsResult {
  const [groups, setGroups] = useState<EmailGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(() => new Set(serverSelectedGroupIds ?? []));
  const [sharedInboxes, setSharedInboxes] = useState<SharedInbox[]>(() =>
    emailsToSharedInboxes(serverSharedInboxes ?? []),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasFetchedRef = useRef(false);
  const hasInitialSyncRef = useRef(false);

  // Sync from server settings only on initial load (before user makes changes).
  // After save, we don't want stale server refetch data to overwrite local state.
  useEffect(() => {
    if (hasInitialSyncRef.current) return;
    if (serverSelectedGroupIds != null || serverSharedInboxes != null) {
      hasInitialSyncRef.current = true;
      if (serverSelectedGroupIds != null) {
        setSelectedGroupIds(new Set(serverSelectedGroupIds));
      }
      if (serverSharedInboxes != null) {
        setSharedInboxes(emailsToSharedInboxes(serverSharedInboxes));
      }
    }
  }, [serverSelectedGroupIds, serverSharedInboxes]);

  // Mark as initialized on mount
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Stop loading when graphClient is unavailable so the UI doesn't hang.
  // If graphClient arrives later, the fetch effect below will re-trigger.
  useEffect(() => {
    if (isInitialized && !graphClient) {
      setIsLoading(false);
    }
  }, [isInitialized, graphClient]);

  const fetchGroups = useCallback(async () => {
    if (!graphClient) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const client = graphClient as {
        api: (path: string) => {
          select: (fields: string) => { get: () => Promise<{ value: Record<string, string | boolean>[] }> };
        };
      };
      const response = await client
        .api('/me/memberOf')
        .select('id,displayName,mail,deletedDateTime,isArchived,mailEnabled,hideFromOutlookClients')
        .get();

      const emailGroups: EmailGroup[] = response.value
        .filter(
          (group: Record<string, string | boolean>) =>
            group.mailEnabled &&
            group.mail &&
            !group.hideFromOutlookClients &&
            !group.isArchived &&
            !group.deletedDateTime,
        )
        .map((group) => ({
          id: group.id as string,
          displayName: (group.displayName as string) || 'Unnamed Group',
          mail: group.mail as string,
        }));

      setGroups(emailGroups);

      browserLogger.debug('[Chat Settings] Fetched groups', { count: emailGroups.length });
    } catch (err) {
      browserLogger.error('[Chat Settings] Error fetching groups', { error: err });
      setError('Failed to load email groups');
    } finally {
      setIsLoading(false);
    }
  }, [graphClient]);

  // Fetch from API only if we don't have cached data and graphClient is ready
  useEffect(() => {
    if (isInitialized && graphClient && groups.length === 0 && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchGroups();
    }
  }, [isInitialized, graphClient, groups.length, fetchGroups]);

  // Toggle group selection (local only — persisted on save)
  const toggleGroup = useCallback((groupId: string) => {
    setSelectedGroupIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  const selectAllGroups = useCallback(() => {
    setSelectedGroupIds(new Set(groups.map((g) => g.id)));
  }, [groups]);

  const deselectAllGroups = useCallback(() => {
    setSelectedGroupIds(new Set());
  }, []);

  const addSharedInbox = useCallback(
    (email: string): boolean => {
      const trimmedEmail = email.trim().toLowerCase();

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return false;
      }

      if (sharedInboxes.some((inbox) => inbox.email === trimmedEmail)) {
        return false;
      }

      const newInbox: SharedInbox = {
        id: uuidv4(),
        email: trimmedEmail,
        enabled: true,
      };

      setSharedInboxes([...sharedInboxes, newInbox]);
      return true;
    },
    [sharedInboxes],
  );

  const removeSharedInbox = useCallback(
    (id: string) => {
      setSharedInboxes(sharedInboxes.filter((inbox) => inbox.id !== id));
    },
    [sharedInboxes],
  );

  const toggleSharedInbox = useCallback(
    (id: string) => {
      setSharedInboxes(sharedInboxes.map((inbox) => (inbox.id === id ? { ...inbox, enabled: !inbox.enabled } : inbox)));
    },
    [sharedInboxes],
  );

  // Dirty check: compare local state against server-persisted values
  const isDirty = useMemo(() => {
    const serverGroupSet = new Set(serverSelectedGroupIds ?? []);
    if (selectedGroupIds.size !== serverGroupSet.size || [...selectedGroupIds].some((id) => !serverGroupSet.has(id))) {
      return true;
    }
    const localEmails = sharedInboxesToEmails(sharedInboxes);
    const serverEmails = serverSharedInboxes ?? [];
    return localEmails.length !== serverEmails.length || localEmails.some((e, i) => e !== serverEmails[i]);
  }, [selectedGroupIds, serverSelectedGroupIds, sharedInboxes, serverSharedInboxes]);

  // Persist all changes to the server in a single mutation
  const save = useCallback(() => {
    const groupIds = Array.from(selectedGroupIds);
    const emails = sharedInboxesToEmails(sharedInboxes);
    onSaveCallback?.({ groupIds, sharedInboxes: emails });
    browserLogger.debug('[Chat Settings] Saved settings', { groupIds, sharedInboxes: emails });
  }, [selectedGroupIds, sharedInboxes, onSaveCallback]);

  // Revert local state back to server-persisted values
  const resetChanges = useCallback(() => {
    setSelectedGroupIds(new Set(serverSelectedGroupIds ?? []));
    setSharedInboxes(emailsToSharedInboxes(serverSharedInboxes ?? []));
  }, [serverSelectedGroupIds, serverSharedInboxes]);

  const refreshGroups = useCallback(async () => {
    await fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    selectedGroupIds,
    sharedInboxes,
    isLoading,
    error,
    isDirty,
    toggleGroup,
    selectAllGroups,
    deselectAllGroups,
    addSharedInbox,
    removeSharedInbox,
    toggleSharedInbox,
    save,
    resetChanges,
    refreshGroups,
  };
}
